import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Chip,
  Alert,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Receipt as ReceiptIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { billingApi } from '../../services/api';
import { formatCurrency } from '../../utils/helpers';

const BillingAlertCard = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const response = await billingApi.getSummary();
        setSummary(response.data);
      } catch (error) {
        console.error('Failed to fetch billing summary:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  if (loading || !summary) return null;

  const hasAlerts = summary.invoicesDueToday > 0 || summary.overdueInvoices > 0;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Billing Overview
          </Typography>
          <Button
            size="small"
            endIcon={<ArrowIcon sx={{ fontSize: 14 }} />}
            onClick={() => navigate('/billing')}
            sx={{ fontSize: '0.7rem', py: 0.25 }}
          >
            View All
          </Button>
        </Box>

        {/* Revenue Stats */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mb: 1.5 }}>
          <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
              Expected
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              {formatCurrency(summary.expectedRevenue)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
            <Typography variant="caption" color="success.dark" display="block" sx={{ fontSize: '0.7rem' }}>
              Collected
            </Typography>
            <Typography variant="body2" fontWeight={700} color="success.dark">
              {formatCurrency(summary.collectedRevenue)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
            <Typography variant="caption" color="warning.dark" display="block" sx={{ fontSize: '0.7rem' }}>
              Pending
            </Typography>
            <Typography variant="body2" fontWeight={700} color="warning.dark">
              {formatCurrency(summary.pendingRevenue)}
            </Typography>
          </Box>
        </Box>

        {/* Alerts */}
        {hasAlerts ? (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
              Action Required
            </Typography>
            <List disablePadding sx={{ mt: 0.5 }}>
              {summary.overdueInvoices > 0 && (
                <ListItem
                  sx={{
                    px: 1,
                    py: 0.75,
                    bgcolor: 'error.light',
                    borderRadius: 0.5,
                    mb: 0.5,
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate('/billing?status=overdue')}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <WarningIcon sx={{ color: 'error.dark', fontSize: 18 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${summary.overdueInvoices} overdue invoice${summary.overdueInvoices > 1 ? 's' : ''}`}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      color: 'error.dark',
                      fontSize: '0.8rem',
                    }}
                  />
                  <Chip
                    label="Overdue"
                    size="small"
                    color="error"
                    sx={{ fontWeight: 600, height: 22, fontSize: '0.7rem' }}
                  />
                </ListItem>
              )}

              {summary.invoicesDueToday > 0 && (
                <ListItem
                  sx={{
                    px: 1,
                    py: 0.75,
                    bgcolor: 'warning.light',
                    borderRadius: 0.5,
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate('/billing?status=due')}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <ReceiptIcon sx={{ color: 'warning.dark', fontSize: 18 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${summary.invoicesDueToday} invoice${summary.invoicesDueToday > 1 ? 's' : ''} due today`}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      color: 'warning.dark',
                      fontSize: '0.8rem',
                    }}
                  />
                  <Chip
                    label="Due Today"
                    size="small"
                    color="warning"
                    sx={{ fontWeight: 600, height: 22, fontSize: '0.7rem' }}
                  />
                </ListItem>
              )}
            </List>
          </Box>
        ) : (
          <Alert severity="success" sx={{ borderRadius: 1, py: 0.5, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
            All invoices are up to date!
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default BillingAlertCard;
